'use client';

import React, { useState } from 'react';
import useSWR from 'swr';

interface BrowseResponse {
  roots?: { name: string; path: string }[];
  path?: string;
  parent?: string | null;
  dirs?: { name: string; path: string }[];
  imageCount?: number;
  error?: string;
}

const fetcher = (url: string) => fetch(url).then((r) => r.json());

export function BrowseDialog({
  initialPath,
  onSelect,
  onClose,
}: {
  initialPath: string | null;
  onSelect: (path: string) => void;
  onClose: () => void;
}) {
  const [current, setCurrent] = useState<string | null>(initialPath);
  const { data, isLoading } = useSWR<BrowseResponse>(
    current ? `/api/browse?path=${encodeURIComponent(current)}` : '/api/browse',
    fetcher,
    { keepPreviousData: true }
  );

  // An invalid start path (e.g. hand-typed) falls back to the roots list
  if (data?.error && current) setCurrent(null);

  const crumbs = current ? current.split('/').filter(Boolean) : [];

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onClose}>
      <div
        className="bg-white rounded-lg shadow-xl w-[640px] max-w-[92vw] h-[70vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <span className="text-lg font-semibold">Choose a photo folder</span>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700">✕</button>
        </div>

        {/* Breadcrumbs */}
        <div className="px-4 py-2 border-b border-gray-100 flex flex-wrap items-center gap-1 text-sm">
          <button
            onClick={() => setCurrent(null)}
            className="text-gray-500 hover:underline"
          >
            Locations
          </button>
          {crumbs.map((part, i) => (
            <React.Fragment key={i}>
              <span className="text-gray-300">/</span>
              <button
                onClick={() => setCurrent('/' + crumbs.slice(0, i + 1).join('/'))}
                className={i === crumbs.length - 1
                  ? 'font-semibold text-gray-800'
                  : 'text-gray-500 hover:underline'}
              >
                {part}
              </button>
            </React.Fragment>
          ))}
        </div>

        {/* Folder list */}
        <div className="flex-1 overflow-y-auto px-2 py-2">
          {isLoading && !data ? (
            <div className="text-sm text-gray-400 p-3">Loading…</div>
          ) : data?.roots ? (
            data.roots.map((r) => (
              <FolderRow key={r.path} name={r.name} hint={r.path} onOpen={() => setCurrent(r.path)} />
            ))
          ) : (
            <>
              {data?.parent !== null && data?.parent !== undefined && (
                <FolderRow name=".." hint="up one level" onOpen={() => setCurrent(data.parent!)} />
              )}
              {data?.dirs?.map((d) => (
                <FolderRow key={d.path} name={d.name} onOpen={() => setCurrent(d.path)} />
              ))}
              {data?.dirs?.length === 0 && (
                <div className="text-sm text-gray-400 p-3">No subfolders</div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-gray-200">
          <span className="text-xs text-gray-500">
            {current
              ? `${data?.imageCount ?? 0} images in this folder (subfolders are included when scanning)`
              : 'Pick a location to start'}
          </span>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm rounded border border-gray-300">
              Cancel
            </button>
            <button
              onClick={() => current && onSelect(current)}
              disabled={!current}
              className="px-4 py-2 rounded-md text-white text-sm font-medium disabled:opacity-40"
              style={{ backgroundColor: 'rgb(51, 102, 204)' }}
            >
              Use This Folder
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function FolderRow({ name, hint, onOpen }: { name: string; hint?: string; onOpen: () => void }) {
  return (
    <button
      onClick={onOpen}
      className="w-full flex items-center gap-2 px-3 py-2 rounded hover:bg-gray-100 text-left text-sm"
    >
      <FolderIcon />
      <span className="flex-1 truncate">{name}</span>
      {hint && <span className="text-xs text-gray-400 font-mono">{hint}</span>}
    </button>
  );
}

function FolderIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgb(51, 102, 204)" strokeWidth="2">
      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
    </svg>
  );
}
